import { GoogleGenAI } from "@google/genai";
import { GoogleGenerativeAI } from "@google/generative-ai";
import { FirebaseApp, initializeApp } from "firebase/app";
import {
  Auth,
  createUserWithEmailAndPassword,
  getAuth,
  signInWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  DocumentSnapshot,
  Firestore,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  where,
  updateDoc,
  runTransaction,
  increment,
  writeBatch,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { getStorage, ref, deleteObject } from "firebase/storage";
import {
  Album,
  Comment,
  EditingTurn,
  InteractiveEditingData,
  LibraryItem,
} from "./types/library";
import { Effect as UserEffect, Effect } from "./types/effects";
import {
  publishContent,
  UserData,
  UserGlobalData,
} from "./store/dreamworldSlice";
import { authWithPiNetwork } from "./utils/PiIntegration";
import { store } from "./store/store";
import { Event, Submission } from "./types/events";
import { Notification } from "./types/notifications";
import { plans } from "./data/plans";
import { effects } from "./data/effects";
import { photoEffects } from "./data/photo-effects";
import { v4 } from "uuid";

const GOOGLE_API_KEY = "AIzaSyDKqbfR_vItoj4GvwzBv8lBfyJajcEuqaA"; // TODO: Replace with your actual API key
const imageAI = new GoogleGenAI({
  apiKey: "AIzaSyDKqbfR_vItoj4GvwzBv8lBfyJajcEuqaA",
});
const genAI = new GoogleGenerativeAI(GOOGLE_API_KEY);

// Replace the global PLACEHOLDER_IMAGE_URL with constants where appropriate,
// or keep if used for other specific placeholders.
// For user signup, we will use DEFAULT_PROFILE_PIC_URL.
// For bot creation, we will use DEFAULT_BOT_PLACEHOLDER_IMAGE_URL initially,
// then update it with the uploaded image URL.

export interface ChatMessage {
  senderId: string;
  senderName: string;
  text: string;
  timestamp: number;
  imageUrl: string | null;
}

export interface SavedChat {
  id?: string;
  userId: string;
  botId: string;
  title: string | null;
  description: string | null;
  imageUrl: string;
  messages: ChatMessage[];
  timestamp: number;
  isPublic: boolean; // Added isPublic property
}

export interface Chat {
  id: string;
  title: string;
  description?: string; // Add description to Chat interface
  isPublic: boolean;
  participants: string[]; // Array of user UIDs
  lastMessage?: ChatMessage;
  createdAt: number;
}

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDMmqvb_kwcBGh4hEIw5nn8cMODHtU8KGI",

  authDomain: "dreamworld0x.firebaseapp.com",

  projectId: "dreamworld0x",

  storageBucket: "dreamworld0x.firebasestorage.app",

  messagingSenderId: "653043507752",

  appId: "1:653043507752:web:0530ebaf0313b9cace0b1d",

  measurementId: "G-4K6CF9H0LK",
};

// Initialize Firebase ONCE
const firebaseApp = initializeApp(firebaseConfig);
const firestoreDb = getFirestore(firebaseApp);
const firebaseAuth = getAuth(firebaseApp);
const storage = getStorage(firebaseApp);

class DreamWorldProvider {
  private app: FirebaseApp;
  private db: Firestore;
  private auth: Auth;

  constructor() {
    // Use the already initialized instances
    this.app = firebaseApp;
    this.db = firestoreDb;
    this.auth = firebaseAuth;
  }

  // Helper function to recursively replace undefined values with null
  private replaceUndefinedWithNull(obj: any): any {
    if (obj === undefined) {
      return null;
    }

    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.replaceUndefinedWithNull(item));
    }

    // For objects, recursively process each property
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      cleaned[key] = this.replaceUndefinedWithNull(value);
    }

    return cleaned;
  }

  async createEffect(
    effect: Omit<UserEffect, "id" | "useCount" | "createdAt">
  ): Promise<void> {
    const effectsCollection = collection(this.db, "effects");
    const newEffectRef = doc(effectsCollection);

    const newEffect: UserEffect = {
      ...effect,
      id: newEffectRef.id,
      useCount: 0,
      createdAt: Timestamp.now(),
    };

    await setDoc(newEffectRef, newEffect);

    const userRef = doc(this.db, "users", effect.creatorId);
    await updateDoc(userRef, {
      effectsCreatedCount: increment(1),
    });
  }

  async incrementEffectUseCount(
    effectId: string,
    userId: string
  ): Promise<void> {
    const effectRef = doc(this.db, "effects", effectId);
    const userEffectRef = doc(this.db, "users", userId, "effects", effectId);

    await runTransaction(this.db, async (transaction) => {
      const userEffectDoc = await transaction.get(userEffectRef);

      if (userEffectDoc.exists()) {
        transaction.update(userEffectRef, { useCount: increment(1) });
      } else {
        transaction.set(userEffectRef, { useCount: 1 });
      }

      transaction.update(effectRef, { useCount: increment(1) });
    });
  }

  async getUserCreatedEffects(userId: string): Promise<UserEffect[]> {
    const effectsCollection = collection(this.db, "effects");
    const q = query(effectsCollection, where("creatorId", "==", userId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => doc.data() as UserEffect);
  }

  async getMostUsedEffects(
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    effects: UserEffect[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    const effectsCollection = collection(this.db, "effects");
    const constraints: QueryConstraint[] = [
      orderBy("useCount", "desc"),
      limit(20),
    ];

    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }

    const q = query(effectsCollection, ...constraints);
    const querySnapshot = await getDocs(q);
    const effects = await Promise.all(
      querySnapshot.docs.map(async (document) => {
        const effect = document.data() as UserEffect;
        if (userId) {
          const userEffectDoc = await getDoc(
            doc(this.db, "users", userId, "effects", effect.id)
          );
          if (userEffectDoc.exists()) {
            effect.userUsage = {
              useCount: userEffectDoc.data().useCount,
            };
          }
        }
        return effect;
      })
    );
    const newLastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

    return { effects, lastVisible: newLastVisible };
  }

  async getRecentEffects(
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    effects: UserEffect[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    const effectsCollection = collection(this.db, "effects");
    const constraints: QueryConstraint[] = [
      orderBy("createdAt", "desc"),
      limit(20),
    ];

    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }

    const q = query(effectsCollection, ...constraints);
    const querySnapshot = await getDocs(q);
    const effects = await Promise.all(
      querySnapshot.docs.map(async (document) => {
        const effect = document.data() as UserEffect;
        if (userId) {
          const userEffectDoc = await getDoc(
            doc(this.db, "users", userId, "effects", effect.id)
          );
          if (userEffectDoc.exists()) {
            effect.userUsage = {
              useCount: userEffectDoc.data().useCount,
            };
          }
        }
        return effect;
      })
    );
    const newLastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

    return { effects, lastVisible: newLastVisible };
  }

  async getEffects(
    type: "video" | "photo",
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    effects: Effect[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const constraints: QueryConstraint[] = [
        where("type", "==", type),
        orderBy("id"),
        limit(20),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(this.db, "effects"), ...constraints);

      const querySnapshot = await getDocs(q);
      const effects = await Promise.all(
        querySnapshot.docs.map(async (document) => {
          const effect = document.data() as Effect;
          if (userId) {
            const userEffectDoc = await getDoc(
              doc(this.db, "users", userId, "effects", effect.id)
            );
            if (userEffectDoc.exists()) {
              effect.userUsage = {
                useCount: userEffectDoc.data().useCount,
              };
            }
          }
          return effect;
        })
      );

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      return { effects, lastVisible: newLastVisible };
    } catch (error) {
      console.error(`Error fetching ${type} effects:`, error);
      throw new Error(`Failed to fetch ${type} effects.`);
    }
  }

  // Example function to get data from Firestore
  async getDreamWorldData() {
    const querySnapshot = await getDocs(collection(this.db, "DreamWorld"));
    const data: any[] = [];
    querySnapshot.forEach((doc) => {
      data.push(doc.data());
    });
    return data;
  }

  async signInWithPi(demo?: boolean): Promise<UserGlobalData> {
    let username: string;

    if (
      window.location.hostname.includes("localhost") ||
      window.location.hostname.includes("dreworld") ||
      demo
    ) {
      username = "RichAdams";
    } else {
      try {
        const piAuthResult = await authWithPiNetwork();
        username = piAuthResult.username;
      } catch (error) {
        console.error("Pi Network authentication failed", error);
        throw new Error("Failed to authenticate with Pi Network.");
      }
    }

    const email = `${username}@dreamworld.com`;
    // NOTE: Using a generic password. This is not secure and should be
    // replaced with a more robust authentication mechanism for production.
    const password = "password123";

    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      );
      const user = userCredential.user;
      const userDocRef = doc(this.db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        return { user, userData: userDocSnap.data() as UserData };
      } else {
        const newUser_data: UserData = {
          uid: user.uid,
          username,
          email,
          createdAt: Date.now(),
        };
        await setDoc(userDocRef, newUser_data);
        return { user, userData: newUser_data };
      }
    } catch (error: any) {
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/invalid-credential"
      ) {
        try {
          const userCredential = await createUserWithEmailAndPassword(
            this.auth,
            email,
            password
          );
          const user = userCredential.user;
          const newUser_data: UserData = {
            uid: user.uid,
            username,
            email,
            createdAt: Date.now(),
          };
          await setDoc(doc(this.db, "users", user.uid), newUser_data);
          return { user, userData: newUser_data };
        } catch (createError) {
          console.error("Error creating user:", createError);
          throw new Error("Failed to create user.");
        }
      } else {
        console.error("Error signing in:", error);
        throw new Error("Failed to sign in.");
      }
    }
  }

  async signInWithEmail(email: string, password: string): Promise<UserGlobalData> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password
      );
      const user = userCredential.user;
      const userDocRef = doc(this.db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        return { user, userData: userDocSnap.data() as UserData };
      } else {
        const username = email.split("@")[0];
        const newUserData: UserData = {
          uid: user.uid,
          username,
          email,
          createdAt: Date.now(),
        };
        await setDoc(userDocRef, newUserData);
        return { user, userData: newUserData };
      }
    } catch (error: any) {
      if (
        error.code === "auth/user-not-found" ||
        error.code === "auth/invalid-credential"
      ) {
        try {
          const userCredential = await createUserWithEmailAndPassword(
            this.auth,
            email,
            password
          );
          const user = userCredential.user;
          const username = email.split("@")[0];
          const newUserData: UserData = {
            uid: user.uid,
            username,
            email,
            createdAt: Date.now(),
          };
          await setDoc(doc(this.db, "users", user.uid), newUserData);
          return { user, userData: newUserData };
        } catch (createError) {
          console.error("Error creating user:", createError);
          throw new Error("Failed to create account. Please try again.");
        }
      } else if (error.code === "auth/wrong-password") {
        throw new Error("Incorrect password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        throw new Error("Too many attempts. Please try again later.");
      } else if (error.code === "auth/weak-password") {
        throw new Error("Password must be at least 6 characters.");
      } else if (error.code === "auth/invalid-email") {
        throw new Error("Please enter a valid email address.");
      } else if (error.code === "auth/email-already-in-use") {
        throw new Error("This email is already in use. Try signing in instead.");
      } else {
        console.error("Error signing in:", error);
        throw new Error("Failed to sign in. Please try again.");
      }
    }
  }

  async sendVerificationEmail(): Promise<void> {
    const user = this.auth.currentUser;
    if (user && !user.emailVerified) {
      await sendEmailVerification(user);
    }
  }

  async autoSignInFromSession(): Promise<UserGlobalData> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error("No active session found.");
    }
    const userDocRef = doc(this.db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);
    if (userDocSnap.exists()) {
      return { user, userData: userDocSnap.data() as UserData };
    } else {
      throw new Error("User data not found.");
    }
  }

  async getLibraryItem(contentId: string): Promise<LibraryItem | null> {
    try {
      const itemRef = doc(this.db, "libraries", contentId);
      const itemSnap = await getDoc(itemRef);
      if (itemSnap.exists()) {
        const libraryItem = {
          id: itemSnap.id,
          ...itemSnap.data(),
        } as LibraryItem;
        if (libraryItem.effectId) {
          const effect = await this.getEffect(libraryItem.effectId);
          if (effect) {
            libraryItem.effect = effect;
          }
        }
        return libraryItem;
      }
      return null;
    } catch (error) {
      console.error("Error fetching library item:", error);
      throw new Error("Failed to fetch library item.");
    }
  }

  async saveLibraryItem(
    item: Omit<LibraryItem, "createdAt" | "isPublished" | "title"> & {
      prompt: string;
    }
  ): Promise<string> {
    const docRef = doc(this.db, "libraries", item.id); // use item.id as doc id
    await setDoc(docRef, {
      ...item,
      title: item.prompt,
      isPublished: false,
      createdAt: Timestamp.now(),
    });

    // Update user analytics
    const analyticsUpdate: Partial<UserData> = {
      lastGenerationMedia: item.thumbnailUrl,
    };
    if (item.type === "Image") {
      analyticsUpdate.imagesGeneratedCount = 1;
    } else if (item.type === "Video") {
      analyticsUpdate.videoGeneratedCount = 1;
    }
    await this.updateUserAnalytics(item.ownerId, analyticsUpdate);

    return docRef.id;
  }

  async saveUploadItem(
    item: Omit<LibraryItem, "isPublished">
  ): Promise<string> {
    const docRef = doc(this.db, "uploads", item.id);
    await setDoc(docRef, {
      ...item,
      isPublished: false,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  }

  /**
   * Fetches a paginated list of library items for a user.
   * Can optionally filter the items by a specific albumId.
   *
   * @param userId The ID of the content owner.
   * @param lastVisible The last document snapshot from the previous page, for pagination.
   * @param albumId (Optional) The ID of the album to filter by.
   * @returns A promise that resolves to an object with the items and the new lastVisible document.
   */
  async getLibraryContent(
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null,
    albumId?: string,
    getMax?: boolean
  ): Promise<{
    items: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const constraints = [where("ownerId", "==", userId)];
      if (albumId) {
        constraints.push(where("albumId", "==", albumId));
      }

      let q;
      if (lastVisible) {
        q = query(
          collection(this.db, "libraries"),
          ...constraints,
          orderBy("createdAt", "desc"),
          startAfter(lastVisible),
          limit(getMax ? 1000 : 20)
        );
      } else {
        q = query(
          collection(this.db, "libraries"),
          ...constraints,
          orderBy("createdAt", "desc"),
          limit(getMax ? 1000 : 20)
        );
      }

      const querySnapshot = await getDocs(q);
      var items: LibraryItem[] = [];
      for (let i = 0; i < querySnapshot.docs?.length; i++) {
        var content = querySnapshot.docs[i].data() as Omit<
          LibraryItem,
          "Creator"
        >;
        content.creator = await this.getContentCreatorData(content?.ownerId);
        items.push(content);
      }

      const itemsWithLikes = await Promise.all(
        items.map(async (item) => {
          const likeRef = doc(this.db, `libraries/${item.id}/likes/${userId}`);
          const likeSnap = await getDoc(likeRef);
          return { ...item, isLiked: likeSnap.exists() };
        })
      );

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const enrichedItems =
        await this.enrichLibraryItemsWithEffects(itemsWithLikes);

      return { items: enrichedItems, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching library content:", error);
      throw new Error("Failed to fetch library content.");
    }
  }
  async publishContent({
    id,
    title,
    description,
    tags,
    effectId,
  }: {
    id: string;
    title: string;
    description: string;
    tags: string[];
    effectId?: string;
  }): Promise<void> {
    try {
      const itemRef = doc(this.db, "libraries", id);
      const itemSnap = await getDoc(itemRef);
      if (!itemSnap.exists()) {
        throw new Error("Library item not found");
      }
      const item = itemSnap.data() as LibraryItem;

      const updateData: any = {
        isPublished: true,
        title,
        description,
        tags,
      };

      if (effectId) {
        updateData.effectId = effectId;
      }

      await updateDoc(itemRef, updateData);

      // Update user analytics
      await this.updateUserAnalytics(item.ownerId, {
        contentPublishedCount: 1,
      });

      store.dispatch(
        publishContent({ id, title, description, tags, effectId })
      );
    } catch (error) {
      console.log("Error while publishing content", error);
      throw error;
    }
  }

  async createAlbum(
    album: Omit<Album, "id" | "itemCount" | "createdAt">
  ): Promise<Album> {
    const albumsRef = collection(this.db, "albums");
    const q = query(albumsRef, where("ownerId", "==", album.ownerId));
    const userAlbums = await getDocs(q);
    if (userAlbums.size >= 100) {
      throw new Error("Album limit reached");
    }
    const creationDate = Timestamp.now();
    const docRef = await addDoc(albumsRef, {
      ...album,
      itemCount: 0,
      createdAt: creationDate,
    });
    return {
      ...(album as Album),
      id: docRef.id,
      itemCount: 0,
      createdAt: creationDate,
    };
  }

  async getAlbums(userId: string): Promise<Album[]> {
    const albums: Album[] = [];
    const q = query(
      collection(this.db, "albums"),
      where("ownerId", "==", userId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      albums.push({ id: doc.id, ...doc.data() } as Album);
    });

    return albums;
  }
  async createEvent(event: Event): Promise<void> {
    const eventsRef = doc(this.db, "events", event.id);
    await setDoc(eventsRef, {
      ...event,
      createdAt: Timestamp.now(),
    });

    // Update user analytics
    await this.updateUserAnalytics(event.creatorId, {
      eventsCreatedCount: 1,
    });
  }
  async getUserEvents(userId: string): Promise<Event[]> {
    const events: Event[] = [];
    const q = query(
      collection(this.db, "events"),
      where("creatorId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      events.push({ id: doc.id, ...doc.data() } as Event);
    });

    return events;
  }
  async getEvents(
    lastVisible?: DocumentSnapshot | null,
    pageSize: number = 20
  ): Promise<{ events: Event[]; lastVisible: DocumentSnapshot | null }> {
    try {
      const events: Event[] = [];
      const constraints: QueryConstraint[] = [
        orderBy("createdAt", "desc"),
        limit(pageSize),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(this.db, "events"), ...constraints);

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        events.push({
          ...data,
          id: doc.id,
          startDate: (data.startDate as Timestamp).toDate(),
          endDate: (data.endDate as Timestamp).toDate(),
          submissionDeadline: (data.submissionDeadline as Timestamp).toDate(),
          winnerAnnouncement: (data.winnerAnnouncement as Timestamp).toDate(),
        } as Event);
      });

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      return { events, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching events:", error);
      throw new Error("Failed to fetch events.");
    }
  }

  async getEvent(eventId: string): Promise<Event | null> {
    try {
      const eventRef = doc(this.db, "events", eventId);
      const eventSnap = await getDoc(eventRef);
      if (eventSnap.exists()) {
        return { id: eventSnap.id, ...eventSnap.data() } as Event;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching event:", error);
      throw new Error("Failed to fetch event.");
    }
  }

  async getEventsByCreator(
    creatorId: string,
    lastVisible?: DocumentSnapshot | null,
    pageSize: number = 20
  ): Promise<{ events: Event[]; lastVisible: DocumentSnapshot | null }> {
    try {
      const events: Event[] = [];
      const constraints: QueryConstraint[] = [
        where("creatorId", "==", creatorId),
        orderBy("createdAt", "desc"),
        limit(pageSize),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(this.db, "events"), ...constraints);

      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        events.push({ id: doc.id, ...doc.data() } as Event);
      });

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      return { events, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching events by creator:", error);
      throw new Error("Failed to fetch events by creator.");
    }
  }
  async getPublishedContent(userUid: string): Promise<LibraryItem[]> {
    try {
      const q = query(
        collection(firestoreDb, "libraries"),
        where("ownerId", "==", userUid),
        where("isPublished", "==", true),
        limit(10)
      );
      const querySnapshot = await getDocs(q);
      const published = querySnapshot.docs.map(
        (doc) => doc.data() as LibraryItem
      );
      const enrichedItems = await this.enrichLibraryItemsWithEffects(published);
      return enrichedItems;
    } catch (error) {
      console.error("Error fetching published content:", error);
      throw new Error("Failed to fetch  published content.");
    }
  }

  async getPublishedContentByEffect(effectId: string): Promise<LibraryItem[]> {
    try {
      const q = query(
        collection(firestoreDb, "libraries"),
        where("effectId", "==", effectId),
        where("isPublished", "==", true),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      const content = querySnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as LibraryItem
      );
      const enrichedContent = await this.enrichLibraryItemsWithEffects(content);
      return enrichedContent;
    } catch (error) {
      console.error("Error fetching published content by effect:", error);
      throw new Error("Failed to fetch published content by effect.");
    }
  }

  async getAlbum(albumId: string): Promise<Album | null> {
    try {
      const albumRef = doc(this.db, "albums", albumId);
      const albumSnap = await getDoc(albumRef);

      if (albumSnap.exists()) {
        return { id: albumSnap.id, ...albumSnap.data() } as Album;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching album:", error);
      throw new Error("Failed to fetch album.");
    }
  }

  async getContent(contentId: string): Promise<LibraryItem | null> {
    return this.getLibraryItem(contentId);
  }

  async toggleLikeContent(contentId: string, userId: string): Promise<void> {
    const contentRef = doc(this.db, "libraries", contentId);
    const userLikeRef = doc(this.db, "users", userId, "likes", contentId);
    const contentLikeRef = doc(
      this.db,
      "libraries",
      contentId,
      "likes",
      userId
    );

    await runTransaction(this.db, async (transaction) => {
      const userLikeDoc = await transaction.get(userLikeRef);
      if (userLikeDoc.exists()) {
        // User has already liked, so we are now unliking
        transaction.update(contentRef, { likes: increment(-1) });
        transaction.delete(userLikeRef);
        transaction.delete(contentLikeRef);
      } else {
        // User has not liked, so we are now liking
        transaction.update(contentRef, { likes: increment(1) });
        transaction.set(userLikeRef, {
          contentId,
          createdAt: Timestamp.now(),
        });
        transaction.set(contentLikeRef, {
          userId,
          createdAt: Timestamp.now(),
        });
      }
    });
  }

  async incrementViewCount(contentId: string): Promise<void> {
    const contentRef = doc(this.db, "libraries", contentId);
    await updateDoc(contentRef, {
      views: increment(1),
    });
  }

  async deleteContent(contentId: string, fileUrl: string): Promise<void> {
    const contentRef = doc(this.db, "libraries", contentId);

    // Delete file from storage
    if (fileUrl) {
      const fileRef = ref(storage, fileUrl);
      try {
        await deleteObject(fileRef);
      } catch (error: any) {
        // It's possible the file doesn't exist, so we can ignore not found errors
        if (error.code !== "storage/object-not-found") {
          console.error("Error deleting file from storage:", error);
          throw error;
        }
      }
    }

    // Delete likes subcollection
    const likesRef = collection(this.db, "libraries", contentId, "likes");
    const likesSnapshot = await getDocs(likesRef);
    const batch = writeBatch(this.db);
    likesSnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    await batch.commit();

    // Delete content document
    await deleteDoc(contentRef);
  }

  async addContentToAlbum(
    content: LibraryItem,
    albumId: string
  ): Promise<void> {
    const albumRef = doc(this.db, "albums", albumId);
    const contentRef = doc(albumRef, "contents", content.id);

    const batch = writeBatch(this.db);
    batch.set(contentRef, content);
    batch.update(albumRef, { itemCount: increment(1) });
    await batch.commit();
  }

  async getContentByTag(
    tagName: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    items: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const constraints: QueryConstraint[] = [
        where("tags", "array-contains", tagName),
        orderBy("createdAt", "desc"),
        limit(20),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(this.db, "libraries"), ...constraints);

      const querySnapshot = await getDocs(q);
      var items: LibraryItem[] = [];
      for (let i = 0; i < querySnapshot.docs?.length; i++) {
        var content = querySnapshot.docs[i].data() as Omit<
          LibraryItem,
          "Creator"
        >;
        content.creator = await this.getContentCreatorData(content?.ownerId);
        items.push(content);
      }
      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const enrichedItems = await this.enrichLibraryItemsWithEffects(items);

      return { items: enrichedItems, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching content by tag:", error);
      throw new Error("Failed to fetch content by tag.");
    }
  }

  async toggleFollowCreator(
    currentUserId: string,
    creatorId: string
  ): Promise<void> {
    const currentUserRef = doc(this.db, "users", currentUserId);
    const creatorRef = doc(this.db, "users", creatorId);
    const creatorFollowerRef = doc(creatorRef, "followers", currentUserId);

    await runTransaction(this.db, async (transaction) => {
      const creatorFollowerDoc = await transaction.get(creatorFollowerRef);

      if (creatorFollowerDoc.exists()) {
        // Unfollow
        transaction.update(currentUserRef, {
          following: arrayRemove(creatorId),
        });
        transaction.update(creatorRef, { followers: increment(-1) });
        transaction.delete(creatorFollowerRef);
      } else {
        // Follow
        transaction.update(currentUserRef, {
          following: arrayUnion(creatorId),
        });
        transaction.update(creatorRef, { followers: increment(1) });
        transaction.set(creatorFollowerRef, {
          userId: currentUserId,
          createdAt: Timestamp.now(),
        });
      }
    });
  }

  async getAlbumContent(
    albumId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    items: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const albumRef = doc(this.db, "albums", albumId);
      const contentsRef = collection(albumRef, "contents");

      let q;
      if (lastVisible) {
        q = query(
          contentsRef,
          orderBy("createdAt", "desc"),
          startAfter(lastVisible),
          limit(20)
        );
      } else {
        q = query(contentsRef, orderBy("createdAt", "desc"), limit(20));
      }

      const querySnapshot = await getDocs(q);
      var items: LibraryItem[] = [];
      for (let i = 0; i < querySnapshot.docs?.length; i++) {
        var content = querySnapshot.docs[i].data() as Omit<
          LibraryItem,
          "Creator"
        >;
        content.creator = await this.getContentCreatorData(content?.ownerId);
        items.push(content);
      }

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const enrichedItems = await this.enrichLibraryItemsWithEffects(items);

      return { items: enrichedItems, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching album content:", error);
      throw new Error("Failed to fetch album content.");
    }
  }

  async getPublishedLibraryContent(
    lastVisible?: DocumentSnapshot<any> | null,
    pageSize: number = 20
  ): Promise<{
    items: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const items: LibraryItem[] = [];
      const constraints: QueryConstraint[] = [
        where("isPublished", "==", true),
        orderBy("createdAt", "desc"),
        limit(pageSize),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(this.db, "libraries"), ...constraints);

      const querySnapshot = await getDocs(q);
      for (let i = 0; i < querySnapshot.docs?.length; i++) {
        var content = querySnapshot.docs[i].data() as Omit<
          LibraryItem,
          "Creator"
        >;
        content.creator = await this.getContentCreatorData(content?.ownerId);
        items.push(content);
      }

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const enrichedItems = await this.enrichLibraryItemsWithEffects(items);

      return { items: enrichedItems, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching published library content:", error);
      throw new Error("Failed to fetch published library content.");
    }
  }

  async getUserPublishedContent(
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null,
    pageSize: number = 20
  ): Promise<{
    items: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const items: LibraryItem[] = [];
      const constraints: QueryConstraint[] = [
        where("isPublished", "==", true),
        where("ownerId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(pageSize),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(collection(this.db, "libraries"), ...constraints);

      const querySnapshot = await getDocs(q);
      for (let i = 0; i < querySnapshot.docs?.length; i++) {
        var content = querySnapshot.docs[i].data() as Omit<
          LibraryItem,
          "Creator"
        >;
        content.creator = await this.getContentCreatorData(content?.ownerId);
        items.push(content);
      }

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const enrichedItems = await this.enrichLibraryItemsWithEffects(items);

      return { items: enrichedItems, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching user published library content:", error);
      throw new Error("Failed to fetch user published library content.");
    }
  }

  async updateUserAnalytics(
    userId: string,
    analytics: Partial<UserData>
  ): Promise<void> {
    const userRef = doc(this.db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const userData = userSnap.data() as UserData;
    const {
      imagesGeneratedCount = 0,
      videoGeneratedCount = 0,
      eventsCreatedCount = 0,
      contentPublishedCount = 0,
    } = userData;

    const newImagesCount =
      imagesGeneratedCount + (analytics.imagesGeneratedCount || 0);
    const newVideosCount =
      videoGeneratedCount + (analytics.videoGeneratedCount || 0);
    const newEventsCount =
      eventsCreatedCount + (analytics.eventsCreatedCount || 0);
    const newContentPublishedCount =
      contentPublishedCount + (analytics.contentPublishedCount || 0);
    const newGenerationCount = newImagesCount + newVideosCount + newEventsCount;

    await updateDoc(userRef, {
      ...analytics,
      imagesGeneratedCount: newImagesCount,
      videoGeneratedCount: newVideosCount,
      eventsCreatedCount: newEventsCount,
      contentPublishedCount: newContentPublishedCount,
      generationCount: newGenerationCount,
      lastGenerationDate: Timestamp.now(),
    });
  }
  async getTrendingCreators(): Promise<UserData[]> {
    const creators: UserData[] = [];
    const q = query(
      collection(this.db, "users"),
      orderBy("lastGenerationDate", "desc"),
      limit(10)
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      creators.push({ ...doc.data() } as UserData);
    });

    return creators;
  }
  async getAllCreators(): Promise<UserData[]> {
    const creators: UserData[] = [];
    const q = query(
      collection(this.db, "users"),
      orderBy("generationCount", "desc")
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      creators.push({ ...doc.data() } as UserData);
    });

    return creators;
  }
  async getContentCreatorData(userId: string): Promise<UserData> {
    try {
      const userRef = doc(this.db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error(`User with ID ${userId} not found`);
      }

      const userData = userSnap.data() as any;

      // Map Firestore fields -> Creator type
      const creator: UserData = userData;

      return creator;
    } catch (error) {
      console.error("Error fetching creator data:", error);
      throw new Error("Failed to fetch content creator data.");
    }
  }
  async getuserData(userId: string): Promise<UserData> {
    try {
      const userRef = doc(this.db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error(`User with ID ${userId} not found`);
      }

      const userData = userSnap.data() as UserData;

      return userData;
    } catch (error) {
      console.error("Error fetching creator data:", error);
      throw new Error("Failed to fetch content creator data.");
    }
  }
  async updateUserData(
    userId: string,
    userData: Partial<UserData>
  ): Promise<void> {
    try {
      const userRef = doc(this.db, "users", userId);
      await updateDoc(userRef, userData);
    } catch (error) {
      console.error("Error updating user data:", error);
      throw new Error("Failed to update user data.");
    }
  }

  async claimDailyGift(userId: string): Promise<UserData> {
    const userRef = doc(this.db, "users", userId);

    try {
      const userData = await runTransaction(this.db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) {
          throw new Error(`User with ID ${userId} not found`);
        }

        const userData = userSnap.data() as UserData;
        const { lastClaimedTimestamp, flux } = userData;

        const now = Timestamp.now();
        let canClaim = false;

        if (lastClaimedTimestamp) {
          const diffInMillis = now.toMillis() - lastClaimedTimestamp.toMillis();
          const diffInHours = diffInMillis / (1000 * 60 * 60);
          if (diffInHours >= 24) {
            canClaim = true;
          }
        } else {
          // First time claiming
          canClaim = true;
        }

        if (canClaim) {
          const newFlux = (flux || 0) + 10;
          transaction.update(userRef, {
            flux: newFlux,
            lastClaimedTimestamp: now,
          });
          return {
            ...userData,
            flux: newFlux,
            lastClaimedTimestamp: now,
          };
        } else {
          // Return current data if cannot claim
          return userData;
        }
      });
      return userData;
    } catch (error) {
      console.error("Error claiming daily gift:", error);
      throw new Error("Failed to claim daily gift.");
    }
  }

  async subscribeToPlan(
    userId: string,
    planId: string,
    billingCycle: "monthly" | "yearly"
  ): Promise<void> {
    const userRef = doc(this.db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const plan = plans.find((p) => p.id === planId);
    if (!plan) {
      throw new Error(`Plan with ID ${planId} not found`);
    }

    const now = new Date();
    const endDate =
      billingCycle === "monthly"
        ? new Date(now.setMonth(now.getMonth() + 1))
        : new Date(now.setFullYear(now.getFullYear() + 1));

    const subscription = {
      planId,
      startDate: Timestamp.now(),
      endDate: Timestamp.fromDate(endDate),
    };

    await updateDoc(userRef, {
      subscription,
      flux: increment(plan.flux),
    });
  }

  async checkSubscription(userId: string): Promise<void> {
    const userRef = doc(this.db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error(`User with ID ${userId} not found`);
    }

    const userData = userSnap.data() as UserData;
    const subscription = userData.subscription;

    if (subscription) {
      const now = new Date();
      if (subscription.endDate.toDate() < now) {
        await updateDoc(userRef, {
          subscription: null,
        });
      }
    }
  }

  async getUser(userId: string): Promise<UserData | null> {
    const userRef = doc(this.db, "users", userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data() as UserData;
    } else {
      return null;
    }
  }

  getGenAI() {
    return genAI;
  }

  async generateTagsFromImage(imageUrl: string): Promise<string> {
    const prompt = `Generate 4 comma-separated tags for the image at this url: ${imageUrl}. The tags should be relevant to the image content and style. Do not use hashtags. Output example: "dog, cute, animal, pet" Note that a tag should not have a space in it. If you want to include multiple words in a tag, use camelCase instead of spaces.`;
    try {
      const tools = [{ urlContext: {} }];
      const response = await imageAI.models.generateContent({
        model: "gemini-2.5-flash",
        config: { tools: [{ urlContext: {} }] },
        contents: prompt,
      });

      const text = response.text;
      return text ?? "";
    } catch (error: any) {
      console.log(error?.message);
      console.error("Error generating tags from image:", error);
      throw new Error("Failed to generate tags from image.");
    }
  }

  getImageAI() {
    return imageAI;
  }

  async getUploads(userId: string): Promise<LibraryItem[]> {
    const uploads: LibraryItem[] = [];
    const q = query(
      collection(this.db, "uploads"),
      where("ownerId", "==", userId),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      uploads.push({ id: doc.id, ...doc.data() } as LibraryItem);
    });

    return uploads;
  }

  async getLikedContent(
    userId: string,
    lastVisible?: DocumentSnapshot<any>,
    pageSize: number = 20
  ): Promise<{
    items: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    const likedContent: LibraryItem[] = [];
    const likesRef = collection(this.db, "users", userId, "likes");
    const constraints: QueryConstraint[] = [
      orderBy("createdAt", "desc"),
      limit(pageSize),
    ];

    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }

    const q = query(likesRef, ...constraints);
    const querySnapshot = await getDocs(q);

    for (const doc of querySnapshot.docs) {
      const contentId = doc.data().contentId;
      const content = await this.getContent(contentId);
      if (content) {
        likedContent.push(content);
      }
    }

    const newLastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

    const enrichedItems =
      await this.enrichLibraryItemsWithEffects(likedContent);

    return { items: enrichedItems, lastVisible: newLastVisible };
  }
  async addInteractiveEditingData(data: InteractiveEditingData): Promise<void> {
    try {
      const docRef = doc(this.db, "interactiveEditings", data.id);
      await setDoc(docRef, data);
    } catch (error) {
      console.error("Error adding interactive editing data:", error);
      throw new Error("Failed to add interactive editing data.");
    }
  }
  async getInteractiveEditingData(
    id: string
  ): Promise<InteractiveEditingData | null> {
    try {
      const docRef = doc(this.db, "interactiveEditings", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return docSnap.data() as InteractiveEditingData;
      } else {
        throw new Error("No such document!");
      }
    } catch (error) {
      console.error("Error fetching interactive editing data:", error);
      throw new Error("Failed to fetch interactive editing data.");
    }
  }
  async updateInteractiveEditingData(
    id: string,
    data: EditingTurn
  ): Promise<void> {
    try {
      const docRef = doc(this.db, "interactiveEditings", id);
      await updateDoc(docRef, {
        editings: arrayUnion(data),
        turnsCount: increment(1),
      });
    } catch (error) {
      console.error("Error updating interactive editing data:", error);
      throw new Error("Failed to update interactive editing data.");
    }
  }

  async deleteEditingTurn(editingId: string, turn: EditingTurn): Promise<void> {
    try {
      const docRef = doc(this.db, "interactiveEditings", editingId);
      await updateDoc(docRef, {
        editings: arrayRemove(turn),
        turnsCount: increment(-1),
      });
    } catch (error) {
      console.error("Error deleting editing turn:", error);
      throw new Error("Failed to delete editing turn.");
    }
  }

  async publishEditingTurn(editingId: string, turnId: string): Promise<void> {
    try {
      const docRef = doc(this.db, "interactiveEditings", editingId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const editingData = docSnap.data() as InteractiveEditingData;
        const turn = editingData.editings.find((t) => t.id === turnId);
        if (turn) {
          turn.isPublished = true;
          await updateDoc(docRef, { editings: editingData.editings });
        }
      }
    } catch (error) {
      console.error("Error publishing editing turn:", error);
      throw new Error("Failed to publish editing turn.");
    }
  }

  async getInteractiveEditings(
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    items: InteractiveEditingData[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const constraints: QueryConstraint[] = [
        where("ownerId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(20),
      ];

      if (lastVisible) {
        constraints.push(startAfter(lastVisible));
      }

      const q = query(
        collection(this.db, "interactiveEditings"),
        ...constraints
      );

      const querySnapshot = await getDocs(q);
      const items = querySnapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() }) as InteractiveEditingData
      );

      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      return { items, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching interactive editings:", error);
      throw new Error("Failed to fetch interactive editings.");
    }
  }

  async addComment(
    contentId: string,
    commentText: string,
    creator: UserData
  ): Promise<Comment> {
    const commentsRef = collection(this.db, `libraries/${contentId}/comments`);
    const newComment: Omit<Comment, "id"> = {
      text: commentText,
      creator,
      createdAt: Timestamp.now(),
    };
    const docRef = await addDoc(commentsRef, newComment);
    return { ...newComment, id: docRef.id };
  }

  async getComments(
    contentId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    comments: Comment[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    const constraints: QueryConstraint[] = [
      orderBy("createdAt", "desc"),
      limit(10),
    ];
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }
    const q = query(
      collection(this.db, `libraries/${contentId}/comments`),
      ...constraints
    );
    const querySnapshot = await getDocs(q);
    const comments = querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Comment
    );
    const newLastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;
    return { comments, lastVisible: newLastVisible };
  }

  async submitToEvent(submissionData: Omit<Submission, "id">): Promise<void> {
    await addDoc(collection(this.db, "submissions"), submissionData);
  }

  async getEventSubmissions(
    eventId: string,
    lastVisible?: DocumentSnapshot<any> | null
  ): Promise<{
    submissions: Submission[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    const submissionsCollection = collection(this.db, "submissions");
    let q = query(
      submissionsCollection,
      where("eventId", "==", eventId),
      orderBy("createdAt", "desc"),
      limit(12)
    );

    if (lastVisible) {
      q = query(
        submissionsCollection,
        where("eventId", "==", eventId),
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(12)
      );
    }

    const querySnapshot = await getDocs(q);
    const fetchedSubmissions = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt as Timestamp,
      } as Submission;
    });

    const newLastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;

    return { submissions: fetchedSubmissions, lastVisible: newLastVisible };
  }

  async getLibraryItems(userId: string): Promise<LibraryItem[]> {
    const libraryCollection = collection(this.db, "library");
    const q = query(libraryCollection, where("ownerId", "==", userId));
    const querySnapshot = await getDocs(q);
    const items = querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as LibraryItem
    );
    return items;
  }

  async getPastEvents(): Promise<Event[]> {
    const eventsCollection = collection(this.db, "events");
    const now = Timestamp.now();
    const q = query(
      eventsCollection,
      where("endDate", "<", now),
      orderBy("endDate", "desc")
    );

    const querySnapshot = await getDocs(q);
    const fetchedEvents = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        startDate: new Date(data?.startDate?.seconds * 1000),
        endDate: new Date(data?.endDate?.seconds * 1000),
        submissionDeadline: new Date(data?.submissionDeadline?.seconds * 1000),
        winnerAnnouncement: new Date(data?.winnerAnnouncement?.seconds * 1000),
      } as Event;
    });

    return fetchedEvents;
  }

  async getNotifications(userId: string): Promise<Notification[]> {
    try {
      const notificationsCollection = collection(this.db, "notifications");
      const q = query(
        notificationsCollection,
        where("recipientId", "==", userId),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(q);
      const notifs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Notification[];

      const enrichedNotifs = await Promise.all(
        notifs.map(async (notif) => {
          const sender = await this.getContentCreatorData(notif.senderId);
          const content = await this.getContent(notif.contentId);
          return { ...notif, sender, content: content ?? undefined };
        })
      );

      return enrichedNotifs;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw new Error("Failed to fetch notifications.");
    }
  }

  async markNotificationsAsRead(userId: string): Promise<void> {
    try {
      const notificationsCollection = collection(this.db, "notifications");
      const q = query(
        notificationsCollection,
        where("recipientId", "==", userId),
        where("read", "==", false)
      );

      const querySnapshot = await getDocs(q);
      const batch = writeBatch(this.db);
      querySnapshot.docs.forEach((docSnap) => {
        batch.update(docSnap.ref, { read: true });
      });
      await batch.commit();
    } catch (error) {
      console.error("Error marking notifications as read:", error);
      throw new Error("Failed to mark notifications as read.");
    }
  }

  async createNotification(
    notification: Omit<Notification, "id" | "timestamp">
  ): Promise<void> {
    try {
      await addDoc(collection(this.db, "notifications"), {
        ...notification,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error creating notification:", error);
      throw new Error("Failed to create notification.");
    }
  }

  onUnreadNotificationsCountChange(
    userId: string,
    callback: (count: number) => void
  ): () => void {
    const notificationsCollection = collection(this.db, "notifications");
    const q = query(
      notificationsCollection,
      where("recipientId", "==", userId),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      callback(snapshot.size);
    });

    return unsubscribe;
  }
  getDb() {
    return this.db;
  }

  async getFollowingPosts(
    following: string[],
    lastVisible?: DocumentSnapshot<any> | null,
    userId?: string
  ): Promise<{
    posts: LibraryItem[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    try {
      const postsRef = collection(this.db, "libraries");
      let q;
      if (lastVisible) {
        q = query(
          postsRef,
          where("ownerId", "in", following),
          where("isPublished", "==", true),
          orderBy("createdAt", "desc"),
          startAfter(lastVisible),
          limit(10)
        );
      } else {
        q = query(
          postsRef,
          where("ownerId", "in", following),
          where("isPublished", "==", true),
          orderBy("createdAt", "desc"),
          limit(10)
        );
      }

      const querySnapshot = await getDocs(q);
      var items: LibraryItem[] = [];
      for (let i = 0; i < querySnapshot.docs?.length; i++) {
        var content = querySnapshot.docs[i].data() as Omit<
          LibraryItem,
          "Creator"
        >;
        content.creator = await this.getContentCreatorData(content?.ownerId);
        items.push(content);
      }

      const itemsWithLikes = await Promise.all(
        items.map(async (item) => {
          const likeRef = doc(this.db, `libraries/${item.id}/likes/${userId}`);
          const likeSnap = await getDoc(likeRef);
          return { ...item, isLiked: likeSnap.exists() };
        })
      );
      const newLastVisible =
        querySnapshot.docs.length > 0
          ? querySnapshot.docs[querySnapshot.docs.length - 1]
          : null;

      const enrichedItems =
        await this.enrichLibraryItemsWithEffects(itemsWithLikes);

      return { posts: enrichedItems, lastVisible: newLastVisible };
    } catch (error) {
      console.error("Error fetching following posts:", error);
      throw new Error("Failed to fetch following posts.");
    }
  }

  async getFollowers(
    userId: string,
    lastVisible?: DocumentSnapshot<any> | null,
    pageSize: number = 10
  ): Promise<{
    followers: UserData[];
    lastVisible: DocumentSnapshot<any> | null;
  }> {
    const followers: UserData[] = [];
    const constraints: QueryConstraint[] = [
      orderBy("createdAt", "desc"),
      limit(pageSize),
    ];
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }
    const q = query(
      collection(this.db, "users", userId, "followers"),
      ...constraints
    );
    const querySnapshot = await getDocs(q);
    for (const doc of querySnapshot.docs) {
      const followerId = doc.data().userId;
      const followerData = await this.getuserData(followerId);
      followers.push(followerData);
    }
    const newLastVisible =
      querySnapshot.docs.length > 0
        ? querySnapshot.docs[querySnapshot.docs.length - 1]
        : null;
    return { followers, lastVisible: newLastVisible };
  }

  async sendTip(
    fromUserId: string,
    toUserId: string,
    amount: number
  ): Promise<void> {
    const fromUserRef = doc(this.db, "users", fromUserId);
    const toUserRef = doc(this.db, "users", toUserId);

    await runTransaction(this.db, async (transaction) => {
      const fromUserDoc = await transaction.get(fromUserRef);
      const toUserDoc = await transaction.get(toUserRef);

      if (!fromUserDoc.exists() || !toUserDoc.exists()) {
        throw new Error("User not found");
      }

      const fromUserData = fromUserDoc.data() as UserData;

      if ((fromUserData.flux || 0) < amount) {
        throw new Error("Insufficient flux");
      }

      transaction.update(fromUserRef, { flux: increment(-amount) });
      transaction.update(toUserRef, { flux: increment(amount) });
    });

    await this.createNotification({
      recipientId: toUserId,
      senderId: fromUserId,
      type: "tip",
      contentId: "", // No content associated with a tip
      read: false,
      amount: amount,
    });
  }

  async processEffectPayment(
    userId: string,
    effect: UserEffect
  ): Promise<void> {
    const fluxCost = effect.userFlux || (effect.type === "photo" ? 5 : 10);
    const userRef = doc(this.db, "users", userId);
    const creatorRef = doc(this.db, "users", effect.creatorId);

    await runTransaction(this.db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      if (!userDoc.exists()) {
        throw new Error("User not found");
      }

      const userData = userDoc.data() as UserData;
      if ((userData.flux || 0) < fluxCost) {
        throw new Error("Insufficient flux");
      }

      const creatorRevenue = Math.floor(fluxCost * 0.6);
      const platformRevenue = fluxCost - creatorRevenue; // To avoid floating point issues

      // Deduct total cost from user
      transaction.update(userRef, { flux: increment(-fluxCost) });

      // Add revenue to creator
      if (effect.creatorId !== "dreamworld") {
        // Don't pay dreamworld itself
        transaction.update(creatorRef, { flux: increment(creatorRevenue) });
      }
    });

    // Send notification to creator
    if (effect.creatorId !== "dreamworld") {
      await this.createNotification({
        recipientId: effect.creatorId,
        senderId: userId,
        type: "effect_payment",
        contentId: effect.id,
        read: false,
        amount: Math.floor(fluxCost * 0.6),
      });
    }
  }

  async getFollowing(
    userId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<{
    following: UserData[];
    totalPages: number;
  }> {
    const following: UserData[] = [];
    const userDoc = await this.getuserData(userId);
    if (userDoc.following) {
      const totalPages = Math.ceil(userDoc.following.length / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = page * pageSize;
      const followingIds = userDoc.following.slice(startIndex, endIndex);
      for (const followingId of followingIds) {
        const followingData = await this.getuserData(followingId);
        following.push(followingData);
      }
      return { following, totalPages };
    }
    return { following, totalPages: 0 };
  }

  async getEffect(effectId: string): Promise<Effect | null> {
    try {
      const effectRef = doc(this.db, "effects", effectId);
      const effectSnap = await getDoc(effectRef);
      if (effectSnap.exists()) {
        const effect = { id: effectSnap.id, ...effectSnap.data() } as Effect;
        if (effect.creatorId) {
          const creator = await this.getContentCreatorData(effect.creatorId);
          effect.creator = creator;
        }
        return effect;
      }
      return null;
    } catch (error) {
      console.error("Error fetching effect:", error);
      throw new Error("Failed to fetch effect.");
    }
  }

  private async enrichLibraryItemsWithEffects(
    items: LibraryItem[]
  ): Promise<LibraryItem[]> {
    const enrichedItems = await Promise.all(
      items.map(async (item) => {
        if (item.effectId) {
          const effect = await this.getEffect(item.effectId);
          if (effect) {
            return { ...item, effect };
          }
        }
        return item;
      })
    );
    return enrichedItems;
  }
}

const dreamWorldProvider = new DreamWorldProvider();
export default dreamWorldProvider;
